<template>
  <div id="production-analysis-dashboard">
    <!-- Header orange banner -->
    <div class="production-analysis-header mb-2 text-white">
      <h3 class="m-0 text-white font-weight-bold">{{ $t('ProductionAnalysis.Title') }}</h3>
    </div>

    <div class="body-block">
      <!-- KPIs Grid -->
      <div class="kpi-grid mb-2">
        <b-card class="kpi-card text-center">
          <div class="kpi-title">{{ $t('ProductionAnalysis.ExpectedTime') }}</div>
          <div class="kpi-value-large">{{ formatTimeMinutes(expectedTime) }}</div>
        </b-card>
        <b-card class="kpi-card text-center">
          <div class="kpi-title">{{ $t('ProductionAnalysis.ProductionTime') }}</div>
          <div class="kpi-value-large">{{ formatTimeMinutes(productionTime) }}</div>
        </b-card>
        <b-card class="kpi-card text-center">
          <div class="kpi-title">{{ $t('ProductionAnalysis.StopTime') }}</div>
          <div class="kpi-value-large text-danger">{{ formatTimeMinutes(stopTime) }}</div>
        </b-card>
        <b-card class="kpi-card text-center">
          <div class="kpi-title">{{ $t('ProductionAnalysis.AvgStandardCycle') }}</div>
          <div class="kpi-value-large">{{ formatCycle(avgStandardCycle) }}</div>
        </b-card>
        <b-card class="kpi-card text-center">
          <div class="kpi-title">{{ $t('ProductionAnalysis.AvgActualCycle') }}</div>
          <div class="kpi-value-large" :class="getCycleColorClass({ standardCycle: avgStandardCycle, actualCycle: avgActualCycle })">
            {{ formatCycle(avgActualCycle) }}
          </div>
        </b-card>
      </div>

      <!-- Filters Inline Bar -->
      <b-card class="mb-2 p-1 filter-bar-card">
        <div class="d-flex flex-wrap align-items-center justify-content-between">
          <div class="d-flex align-items-center flex-wrap">
            <!-- Group By Tabs -->
            <div class="filter-group mr-2 mb-1 mb-md-0">
              <span class="filter-label d-block text-muted font-weight-bold mb-50" style="font-size: 11px;">
                {{ $t('ProductionAnalysis.GroupBy') }}
              </span>
              <b-button-group size="sm">
                <b-button
                  v-for="tab in groupByTabs"
                  :key="tab.value"
                  :variant="selectedGroupBy === tab.value ? 'primary' : 'outline-primary'"
                  class="tab-btn"
                  @click="changeGroupBy(tab.value)"
                >
                  {{ $t(tab.text) }}
                </b-button>
              </b-button-group>
            </div>

            <!-- Date Selector (DatePickerFull) -->
            <div class="filter-group mr-2 mb-1 mb-md-0">
              <span class="filter-label d-block text-muted font-weight-bold mb-50" style="font-size: 11px;">
                {{ $t('ProductionAnalysis.Date') }}
              </span>
              <DatePickerFull
                v-model="dateFilterValue"
                :range="true"
                type="date"
                position="bottom"
                @save="onDateRangeSave"
              >
                <template v-slot:input>
                  <button class="custom-date-picker-btn" type="button">
                    <feather-icon icon="CalendarIcon" size="14" class="mr-25" />
                    <span>{{ formatDatePickerValue(dateFilterValue) }}</span>
                  </button>
                </template>
              </DatePickerFull>
            </div>

            <!-- Search Field -->
            <div class="filter-group mr-2 mb-1 mb-md-0">
              <span class="filter-label d-block text-muted font-weight-bold mb-50" style="font-size: 11px;">
                {{ $t('ProductionAnalysis.ResourceOrderItem') }}
              </span>
              <input
                type="text"
                v-model="filters.searchTerm"
                class="form-control form-control-sm search-input"
                placeholder="Buscar..."
                @input="onSearchInput"
              />
            </div>
          </div>

          <!-- Legend Area -->
          <div class="legend-area d-flex align-items-center mb-1 mb-md-0">
            <span class="legend-title mr-1 text-muted font-weight-bold" style="font-size: 11px;">
              {{ $t('ProductionAnalysis.RealCycle') }}
            </span>
            <div class="legend-item mr-1 d-flex align-items-center" style="font-size: 12px;">
              <span class="dot bg-success mr-25"></span>
              {{ $t('ProductionAnalysis.WithinCycle') }}
            </div>
            <div class="legend-item mr-1 d-flex align-items-center" style="font-size: 12px;">
              <span class="dot bg-warning mr-25"></span>
              {{ $t('ProductionAnalysis.UpTo20') }}
            </div>
            <div class="legend-item d-flex align-items-center" style="font-size: 12px;">
              <span class="dot bg-danger mr-25"></span>
              {{ $t('ProductionAnalysis.Above20') }}
            </div>
          </div>
        </div>
      </b-card>

      <!-- Table Section -->
      <b-card class="chart-card">
        <b-table
          responsive
          :items="filteredItems"
          :fields="tableFields"
          :busy="loading"
          show-empty
          empty-text="Nenhum registro encontrado"
          striped
          hover
          small
          head-variant="light"
          :per-page="perPage"
          :current-page="currentPage"
        >
          <template #cell(expectedQuantity)="data">
            {{ formatNumber(data.value) }}
          </template>

          <template #cell(producedQuantity)="data">
            {{ formatNumber(data.value) }}
          </template>

          <template #cell(refuseQuantity)="data">
            {{ formatNumber(data.value) }}
          </template>

          <template #cell(expectedTime)="data">
            {{ formatTimeMinutes(data.value) }}
          </template>

          <template #cell(productionTime)="data">
            {{ formatTimeMinutes(data.value) }}
          </template>

          <template #cell(stopTime)="data">
            <span :class="data.value > 0 ? 'text-danger font-weight-bold' : ''">
              {{ formatTimeMinutes(data.value) }}
            </span>
          </template>

          <template #cell(standardCycle)="data">
            {{ formatCycle(data.value) }}
          </template>

          <template #cell(actualCycle)="data">
            <span :class="getCycleColorClass(data.item)">
              {{ formatCycle(data.value) }}
            </span>
          </template>

          <template #cell(cycleDelta)="data">
            <span :class="getCycleColorClass(data.item)">
              {{ formatDeltaCycle(data.value) }}
            </span>
          </template>

          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner class="align-middle mr-50"></b-spinner>
              <strong>Carregando dados...</strong>
            </div>
          </template>
        </b-table>

        <div class="d-flex justify-content-end mt-1">
          <b-pagination
            v-model="currentPage"
            :total-rows="filteredItems.length"
            :per-page="perPage"
            align="right"
            size="sm"
            class="my-0"
          />
        </div>
      </b-card>
    </div>

  </div>
</template>

<script>
import moment from "moment";
import { BCard, BRow, BCol, BButton, BButtonGroup, BTable, BSpinner, BPagination } from "bootstrap-vue";
import FeatherIcon from "@core/components/feather-icon/FeatherIcon.vue";
import debounce from "lodash/debounce";
import DatePickerFull from "@/components/components-code/date-time-picker/DatePickerFull.vue";

export default {
  name: "ProductionAnalysisDashboard",
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BButtonGroup,
    BTable,
    BSpinner,
    BPagination,
    FeatherIcon,
    DatePickerFull
  },
  data() {
    return {
      loading: false,
      currentSite: null,

      // Summary data
      expectedTime: 0,
      productionTime: 0,
      stopTime: 0,
      avgStandardCycle: 0,
      avgActualCycle: 0,

      // Items list
      items: [],

      // Pagination
      perPage: 15,
      currentPage: 1,

      selectedGroupBy: 2, // Default to Por Recurso (2)
      dateFilterValue: [new Date(), new Date()],
      
      filters: {
        startDate: "",
        endDate: "",
        searchTerm: ""
      },

      groupByTabs: [
        { value: 1, text: "ProductionAnalysis.ByDay" },
        { value: 2, text: "ProductionAnalysis.ByResource" },
        { value: 3, text: "ProductionAnalysis.ByOrder" },
        { value: 4, text: "ProductionAnalysis.ByItem" }
      ]
    };
  },
  computed: {
    tableFields() {
      const firstColLabel = this.selectedGroupBy === 1 ? this.$t("ProductionAnalysis.Date")
                          : this.selectedGroupBy === 2 ? this.$t("ProductionAnalysis.Resource")
                          : this.selectedGroupBy === 3 ? this.$t("ProductionAnalysis.Order")
                          : this.$t("ProductionAnalysis.Item");
      return [
        { key: "groupIdentifier", label: firstColLabel, sortable: true },
        { key: "expectedQuantity", label: this.$t("ProductionAnalysis.ExpectedQuantity"), sortable: true, class: "text-right" },
        { key: "producedQuantity", label: this.$t("ProductionAnalysis.ProducedQuantity"), sortable: true, class: "text-right" },
        { key: "refuseQuantity", label: this.$t("ProductionAnalysis.RefuseQuantity"), sortable: true, class: "text-right" },
        { key: "expectedTime", label: "T. Previsto", sortable: true, class: "text-right" },
        { key: "productionTime", label: "T. Produção", sortable: true, class: "text-right" },
        { key: "stopTime", label: "T. Parada", sortable: true, class: "text-right" },
        { key: "standardCycle", label: "Ciclo Cad.", sortable: true, class: "text-right" },
        { key: "actualCycle", label: "Ciclo Real", sortable: true, class: "text-right" },
        { key: "cycleDelta", label: "Δ Ciclo", sortable: true, class: "text-right" }
      ];
    },

    filteredItems() {
      // Client-side search filtering as a fallback/enhancement
      const search = this.filters.searchTerm ? this.filters.searchTerm.toLowerCase().trim() : "";
      if (!search) return this.items;

      return this.items.filter((item) => {
        const identifier = item.groupIdentifier ? String(item.groupIdentifier).toLowerCase() : "";
        return identifier.includes(search);
      });
    }
  },
  created() {
    const userInfo = this.$cookies.get("userInfo") || {};
    this.currentSite = userInfo.currentSite || null;

    // Set default dates: default to today
    this.filters.startDate = moment(this.dateFilterValue[0]).format("YYYY-MM-DD");
    this.filters.endDate = moment(this.dateFilterValue[1]).format("YYYY-MM-DD");

    this.debouncedFetchData = debounce(this.fetchData, 500);

    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;

      const userInfo = this.$cookies.get("userInfo") || {};
      const userData = userInfo.userData || {};
      const accountId = userData.accountId || userData.id || userInfo.accountId || null;
      const siteId = this.currentSite || userData.siteId || null;

      const params = {
        SiteId: siteId,
        GroupBy: this.selectedGroupBy,
        SearchTerm: this.filters.searchTerm || null,
        ResourcesIds: null,
        WorkcentersIds: null,
        accountId: accountId
      };

      if (this.filters.startDate) {
        params.StartDate = `${this.filters.startDate}T00:00:00`;
      }
      if (this.filters.endDate) {
        params.EndDate = `${this.filters.endDate}T23:59:59`;
      }

      // Try evoAxiosIns ($http2) and fallback to axiosIns ($http)
      try {
        const response = await this.$http2.get("/api/production-analysis", { params });
        this.parseResponse(response.data);
      } catch (error) {
        console.warn("API request on $http2 failed, trying $http...", error);
        try {
          const response = await this.$http.get("/api/production-analysis", { params });
          this.parseResponse(response.data);
        } catch (fallbackError) {
          console.error("API request failed on both clients:", fallbackError);
          this.$bvToast.toast("Erro ao carregar os dados de análise de produção.", {
            title: "Erro de Conexão",
            variant: "danger",
            solid: true
          });
        }
      } finally {
        this.loading = false;
      }
    },

    parseResponse(responseData) {
      const result = responseData.data || responseData.response || responseData || {};
      this.expectedTime = result.expectedTime || 0;
      this.productionTime = result.productionTime || 0;
      this.stopTime = result.stopTime || 0;
      this.avgStandardCycle = result.avgStandardCycle || 0;
      this.avgActualCycle = result.avgActualCycle || 0;
      this.items = result.items || [];
    },

    changeGroupBy(val) {
      this.selectedGroupBy = val;
      this.currentPage = 1;
      this.fetchData();
    },

    onSearchInput() {
      this.debouncedFetchData();
    },

    onDateRangeSave() {
      if (this.dateFilterValue && this.dateFilterValue.length > 0) {
        this.filters.startDate = moment(this.dateFilterValue[0]).format("YYYY-MM-DD");
        this.filters.endDate = this.dateFilterValue.length > 1
          ? moment(this.dateFilterValue[1]).format("YYYY-MM-DD")
          : moment(this.dateFilterValue[0]).format("YYYY-MM-DD");
      }
      this.currentPage = 1;
      this.fetchData();
    },

    formatDatePickerValue(val) {
      if (!val) return "";
      if (Array.isArray(val)) {
        if (val.length === 1) {
          return moment(val[0]).format("DD/MM/YYYY");
        }
        if (val.length === 2) {
          const start = moment(val[0]).format("DD/MM/YYYY");
          const end = moment(val[1]).format("DD/MM/YYYY");
          if (start === end) return start;
          return `${start} ~ ${end}`;
        }
      }
      return moment(val).format("DD/MM/YYYY");
    },

    // Formatters
    formatNumber(val) {
      if (val == null || isNaN(val)) return "0";
      return Number(val).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 3 });
    },

    formatCycle(val) {
      if (val == null || isNaN(val)) return "0,000s";
      const formatted = Number(val).toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
      return `${formatted}s`;
    },

    formatDeltaCycle(val) {
      if (val == null || isNaN(val)) return "+0,000s";
      const prefix = val > 0 ? "+" : "";
      const formatted = Number(val).toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
      return `${prefix}${formatted}s`;
    },

    formatTimeMinutes(seconds) {
      if (seconds == null || isNaN(seconds)) return "0min";
      return `${Math.round(seconds / 60)}min`;
    },

    getCycleColorClass(item) {
      const std = item.standardCycle;
      const act = item.actualCycle;
      if (std === 0 || std == null) {
        return act === 0 || act == null ? "text-success" : "text-danger";
      }
      const ratio = act / std;
      if (ratio <= 1.0) return "text-success";
      if (ratio <= 1.2) return "text-warning";
      return "text-danger";
    }
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

#production-analysis-dashboard {
  .production-analysis-header {
    background-color: #974900;
    padding: 12px 20px;
    border-radius: 4px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .body-block {
    padding: 4px 0px 20px 0px;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 1.25rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 1200px) {
    .kpi-grid {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  @media (max-width: 768px) {
    .kpi-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 480px) {
    .kpi-grid {
      grid-template-columns: 1fr;
    }
  }

  .kpi-card {
    height: 100%;
    border: none;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.08);
    margin-bottom: 0;
    transition: transform 0.25s ease, box-shadow 0.25s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 30px 0 rgba(34, 41, 47, 0.12);
    }
  }

  .kpi-title {
    font-size: 13px;
    font-weight: 600;
    color: #6e6b7b;
    margin-bottom: 0.5rem;
  }

  .kpi-value-large {
    font-size: 22px;
    font-weight: 700;
    color: #5e5873;
  }

  .filter-bar-card {
    border: none;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.08);
  }

  .filter-label {
    font-size: 11px;
    font-weight: 600;
    color: #6e6b7b;
  }

  .custom-date-picker-btn {
    height: 32px;
    padding: 4px 12px;
    border-radius: 5px;
    background: #fff;
    border: 1px solid #d8d6de;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    color: #6e6b7b;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 130px;

    &:hover {
      border-color: #974900;
      color: #974900;
    }
  }

  .search-input {
    max-width: 220px;
    height: 32px;
  }

  .tab-btn {
    font-weight: 600;

    &.btn-primary {
      background-color: #974900 !important;
      border-color: #974900 !important;
    }

    &.btn-outline-primary {
      color: #974900 !important;
      border-color: #974900 !important;

      &:hover {
        background-color: rgba(151, 73, 0, 0.08) !important;
      }
    }
  }

  .legend-area {
    background-color: #f8f9fa;
    padding: 6px 12px;
    border-radius: 4px;
  }

  .dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }

  .bg-success {
    background-color: #28c76f !important;
  }

  .bg-warning {
    background-color: #ff9f43 !important;
  }

  .bg-danger {
    background-color: #ea5455 !important;
  }

  .chart-card {
    border: none;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.08);
  }
}


</style>
